import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
    findInputSchema,
    FIND_GROUP_OPTIONS,
    ARTEFACT_FIND_GROUP,
    SAMPLE_FIND_GROUP,
    SAMPLE_TYPE_OPTIONS,
    materialOptionGroups,
    FIND_TREATMENT_OPTIONS,
    MATERIAL_LEVELS,
    MATERIAL_LEVEL_OPTIONS,
    PRELIMINARY_DATING_OPTIONS,
    resolveMaterialLevels,
    YES_NO_OPTIONS,
    type FindDocType,
    type FindingDocType,
    type FindingPlanumDocType,
    type FindingProfileDocType,
    formatFindSlipNumber
} from '@ast-data/shared';
import {
    dateColumn,
    type GridColDef,
    naturalSortComparator
} from '../../lib/dataGrid';
import {
    useInlineEditColumn,
    type InlineField
} from '../../components/gridInlineEdit';
import { withRecentValues } from '../../components/recentValues';
import { usePersonUserId } from '../users/personUserId';
import { useRecordActionsColumn } from '../../components/useGridRowActions';
import { useAuditColumns } from '../../components/auditColumns';
import { HIDDEN_AUDIT_COLUMNS } from '../../components/auditFields';
import { useIsoWeekColumn } from '../../components/isoWeekColumn';
import {
    useDetailColumnHeader,
    useFindingRefFilterColumns
} from './findingRefLabels';
import { useFindingRefsColumn } from './useFindingRefsColumn';
import { compareNatural } from '../../utils/compare';

function materialLabel(row: FindDocType): string {
    return [row.materialSubstanceType, row.materialGroup, row.materialName]
        .filter(Boolean)
        .join(' › ');
}

function distinctValues(values: readonly string[]): string[] {
    return [...new Set(values.filter(Boolean))].sort(
        (firstValue, secondValue) => compareNatural(firstValue, secondValue)
    );
}

/**
 * The material levels ABOVE a chosen name. Only the ones the parent chain
 * actually reaches — a blanket `resolveMaterialLevels` would write '' over a
 * Bestimmbarkeit somebody set in the form. Mirrors `fillMaterialAncestors` in
 * FindFormDialog.
 */
function materialAncestors(value: unknown): Record<string, string> {
    const resolved = resolveMaterialLevels(String(value ?? ''));
    const ancestors: Record<string, string> = {};
    for (const level of MATERIAL_LEVELS) {
        if (level === 'materialName') {
            break;
        }
        if (resolved[level]) {
            ancestors[level] = resolved[level];
        }
    }
    return ancestors;
}

// Module-level so the field list's identity is stable across renders.
const FIND_GROUP_EDITOR = {
    kind: 'suggest',
    options: FIND_GROUP_OPTIONS
} as const;
const FIND_TREATMENT_EDITOR = {
    kind: 'suggest',
    options: FIND_TREATMENT_OPTIONS
} as const;
const CONSERVATION_TAKEOVER_EDITOR = {
    kind: 'suggest',
    options: YES_NO_OPTIONS
} as const;
const MATERIAL_NAME_EDITOR = {
    kind: 'suggest',
    options: MATERIAL_LEVEL_OPTIONS.materialName,
    // Every name under its group, the record's own group first — as in the form.
    grouped: (record: Record<string, unknown>) =>
        materialOptionGroups('materialName', record)
} as const;
const SAMPLE_TYPE_EDITOR = {
    kind: 'suggest',
    options: SAMPLE_TYPE_OPTIONS
} as const;

/** The Fundgruppe asks for its own field, as in the form (see FindFormDialog). */
const isArtefact = (record: Record<string, unknown>) =>
    String(record.findGroup ?? '').trim() === ARTEFACT_FIND_GROUP;
const isSample = (record: Record<string, unknown>) =>
    String(record.findGroup ?? '').trim() === SAMPLE_FIND_GROUP;

/**
 * Every remaining Fund field, so the column picker can offer the whole record and
 * the filter panel can narrow by any of it. Hidden by default (see
 * {@link HIDDEN_FIND_DETAIL_COLUMNS}).
 *
 * Left out on purpose: `excavatorId` (the app-user link behind the Ausgräber
 * name) and `planumIds`/`profileIds` (already in the Befund reference column).
 * The audit stamps come from {@link useAuditColumns}, hidden the same way.
 */
const FIND_DETAIL_FIELDS = [
    'strayFindNumber',
    'findBox',
    'area',
    'trench',
    'planum',
    'profile',
    'sampleType',
    'materialDeterminability',
    'materialSubstanceType',
    'materialGroup',
    'materialName',
    'positionToPlanum',
    'positionToProfile',
    'findDescription',
    'preliminaryDating',
    'findTreatment',
    'excavator',
    'conservationRemark',
    'conservationTakeover',
    'macroPhotoLink',
    'remark',
    'internalInformation'
] as const;

/**
 * Bezeichnung and Interne Informationen are written over several lines in the
 * form, so their cell opens a textarea. Without F2: a label is not repeated.
 */
const MULTILINE_FIND_FIELDS: InlineField[] = [
    { field: 'label', editor: { kind: 'multiline' } },
    { field: 'internalInformation', editor: { kind: 'multiline' } }
];

/**
 * The Fund fields editable in the grid — ONE definition, read both by the columns
 * (which editor a cell gets) and by the save (which fields a cell may write, and
 * what each carries along). Splitting the list would let a cell write a field no
 * column offers, or offer one it cannot write.
 *
 * A hook because the Ausgräber needs two things only React can supply: the person
 * options the form offers, and the name→account rule that derives `excavatorId`.
 *
 * Deliberately short: a cell is the right place only for a short value somebody
 * corrects in passing while reading down the column — the two long texts aside
 * (see {@link MULTILINE_FIND_FIELDS}).
 */
export function useInlineFindFields(
    /** Every Fund in the project — for the names already used as Ausgräber. */
    finds: readonly FindDocType[]
): InlineField[] {
    const { resolve } = usePersonUserId();

    const usedBoxes = useMemo(
        () => distinctValues(finds.map((find) => find.findBox)),
        [finds]
    );

    return useMemo(
        () =>
            /**
             * All of them offer their last values on F2: a box of finds is
             * written up in one sitting, and the Boxnummer, the Datierung and the
             * Ausgräber stay put while it is.
             */
            [
                ...withRecentValues<InlineField>(
                    [
                        {
                            field: 'preliminaryDating',
                            editor: {
                                kind: 'suggest',
                                options: PRELIMINARY_DATING_OPTIONS
                            }
                        },
                        {
                            field: 'findBox',
                            // No vocabulary — the boxes in use in this project.
                            editor: { kind: 'suggest', options: usedBoxes }
                        },
                        /**
                         * Not a key: one object bagged several times shares its
                         * Einzelfundnummer, so the last one is what F2 is for.
                         */
                        { field: 'strayFindNumber' },
                        { field: 'findGroup', editor: FIND_GROUP_EDITOR },
                        {
                            field: 'findTreatment',
                            editor: FIND_TREATMENT_EDITOR
                        },
                        {
                            field: 'conservationTakeover',
                            editor: CONSERVATION_TAKEOVER_EDITOR
                        },
                        {
                            /**
                             * Only the material NAME is picked; the levels above it
                             * follow, filled where the parent chain reaches them and
                             * never cleared. The read-only Material column beside it
                             * shows the whole chain.
                             */
                            field: 'materialName',
                            editor: MATERIAL_NAME_EDITOR,
                            derive: materialAncestors,
                            // An Artefakt's field — hidden in the form for the
                            // other Fundgruppen, so read-only here for them.
                            editableWhen: isArtefact
                        },
                        {
                            // A Probe's field, in place of the material.
                            field: 'sampleType',
                            editor: SAMPLE_TYPE_EDITOR,
                            editableWhen: isSample
                        },
                        {
                            field: 'excavator',
                            editor: { kind: 'person' },
                            // The same rule the form saves by, so a name typed here
                            // and there lands on the same account.
                            derive: (value) => ({
                                excavatorId: resolve(String(value ?? ''))
                            })
                        }
                    ],
                    finds
                ),
                ...MULTILINE_FIND_FIELDS
            ],
        [resolve, usedBoxes, finds]
    );
}

/**
 * Detail columns shown ANYWAY — the ones worth a place in the table rather than a
 * tick in the column picker. They earn it by being edited here: a column nobody
 * can see is a column nobody corrects in passing.
 */
const VISIBLE_FIND_DETAIL_FIELDS: readonly string[] = [
    'preliminaryDating',
    'excavator',
    // The picked level: the composed Material column beside it is read-only, so
    // without this one there is nothing to pick IN.
    'materialName'
];

/** `columnVisibilityModel` entries hiding the detail columns by default. */
export const HIDDEN_FIND_DETAIL_COLUMNS: Record<string, boolean> = {
    ...Object.fromEntries(
        FIND_DETAIL_FIELDS.filter(
            (field) => !VISIBLE_FIND_DETAIL_FIELDS.includes(field)
        ).map((field) => [field, false])
    ),
    ...HIDDEN_AUDIT_COLUMNS,
    recordedAt: false
};

export function useFindsColumns({
    findings,
    planums,
    profiles,
    canEdit,
    // Empty for a read-only grid — the cross-project Auswertung passes none.
    inlineFields = [],
    onEdit,
    onDelete,
    onHistory
}: {
    findings: FindingDocType[];
    planums: FindingPlanumDocType[];
    profiles: FindingProfileDocType[];
    canEdit: boolean;
    /** Which columns are edited in the grid — see useInlineFindFields. */
    inlineFields?: readonly InlineField[];
    onEdit: (row: FindDocType) => void;
    onDelete: (row: FindDocType) => void;
    onHistory: (row: FindDocType) => void;
}): GridColDef<FindDocType>[] {
    const intl = useIntl();
    const actionsColumn = useRecordActionsColumn<FindDocType>({
        canEdit,
        onEdit,
        onDelete,
        onHistory
    });

    const auditColumns = useAuditColumns<FindDocType>();
    const inlineEdit = useInlineEditColumn<FindDocType>(
        findInputSchema,
        canEdit
    );
    const inlineOf = (field: string) =>
        inlineFields.find((spec) => spec.field === field);
    const isoWeekColumn = useIsoWeekColumn<FindDocType>(
        (row) => row.recordedAt
    );

    const refsColumn = useFindingRefsColumn<FindDocType>({
        headerId: 'find.field.descriptions',
        findings,
        planums,
        profiles
    });
    // Hidden, filterable Befundnummer/Fläche/Planum/Profil columns, for the
    // filter panel.
    const findingFilterColumns = useFindingRefFilterColumns<FindDocType>(
        findings,
        planums,
        profiles
    );

    const detailHeader = useDetailColumnHeader('find');

    return [
        {
            field: 'findSlipNumber',
            headerName: intl.formatMessage({
                id: 'find.field.findSlipNumber'
            }),
            width: 160,
            // With its Unterfundzettelnummer: "123_2", sorted 123 < 123_2 < 124.
            valueGetter: (_value, row) => formatFindSlipNumber(row),
            sortComparator: naturalSortComparator,
            // Bold like the Befundnummer in the tree grid — it is the row's key.
            cellClassName: 'grid-cell-bold'
        },
        // Straight after the pinned key column: when a record was taken is the
        // first thing anyone reads off a row (same order in every grid).
        isoWeekColumn,
        dateColumn<FindDocType>({
            field: 'recordedAt',
            headerName: intl.formatMessage({ id: 'common.recordedAt' }),
            width: 120
        }),
        {
            field: 'label',
            headerName: intl.formatMessage({ id: 'find.field.label' }),
            flex: 1,
            minWidth: 160,
            ...(inlineOf('label') ? inlineEdit('label', inlineOf('label')) : {})
        },
        { ...refsColumn, width: 240 },
        {
            field: 'material',
            headerName: intl.formatMessage({ id: 'find.field.material' }),
            flex: 1,
            minWidth: 160,
            sortComparator: naturalSortComparator,
            valueGetter: (_value, row) => materialLabel(row)
        },
        {
            field: 'findGroup',
            headerName: intl.formatMessage({ id: 'find.field.findGroup' }),
            width: 130,
            // Already a visible column, so its editor is spread on here rather
            // than in the detail-field loop.
            ...(inlineOf('findGroup')
                ? inlineEdit('findGroup', inlineOf('findGroup'))
                : {})
        },
        ...findingFilterColumns,
        ...FIND_DETAIL_FIELDS.map<GridColDef<FindDocType>>((field) => ({
            field,
            headerName: detailHeader(field),
            width: 160,
            // Only the listed fields get an editor; the rest are read here and
            // written in the form.
            ...(inlineOf(field) ? inlineEdit(field, inlineOf(field)) : {})
        })),
        ...auditColumns,
        actionsColumn
    ];
}
